import React, { RefObject, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { validateFormField } from '../Core/FormFieldValidation';
import { SmartContext } from '../Core/SmartContext';
import { getControlValueFromState, isEmpty } from '../Core/SmartFunctions';
import { DomainElement, SimpleFormControlArguments, State } from '../Core/SmartTypes';
import ErrorControl from './ErrorControl';

const MultiSelectCustomControl2 = (args: SimpleFormControlArguments) => {
    const { state, dispatch } = useContext(SmartContext);
    const { control, dataKey, parentDataKey } = { ...args };
    const data = getControlValueFromState(dataKey, state as State) ?? [];
    const formControlRef = useRef(null); // Note: For providing reference to ErrorControl
    const dropdownRef: RefObject<HTMLDivElement> = useRef(null);

    const parentData = control.props.parentId && getControlValueFromState(parentDataKey + '.' + control.props.parentId, state as State);
    // const [parentId, setParentId] = useState(null);
    const [parentId, setParentId] = useState<string[]>([]);
    if (!isEmpty(parentData) && parentId !== parentData) setParentId(parentData);

    
    useEffect(() => {
        if (!isEmpty(parentData)) {
            const arr = Array.isArray(parentData) ? parentData.map(String) : [String(parentData)];
            setParentId(arr);
        } else {
            setParentId([]);
        }
    }, [parentData]);

    // const controlDomain = getFilteredDomain(control.props.domainCategoryCode, state, parentId);
    const hasParent = Boolean(control.props.parentId);
    const controlDomain = useMemo(() => {
        const domainList =
            (state?.domain?.get(control.props.domainCategoryCode) as DomainElement[]) ?? [];

        if (!hasParent) {
            return domainList;
        }

        if (!parentId.length) {
            return [];
        }
        return domainList.filter(d => parentId.includes(d.parentCode));
    }, [state.domain, control.props.domainCategoryCode, parentId, hasParent]);


    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredDomain, setFilteredDomain] = useState(controlDomain || []);

    const getButtonText = (newVal: any = null, action: string = 'ADD') => {
        if (!controlDomain) return '';
        let selectedNames: any[] = [];

        if (action === 'ADD') {
            selectedNames = controlDomain
                .filter((domain: any) => [...data, newVal].includes(domain.code))
                .map((domain: any) => domain.value);
        }

        if (action === 'REMOVE') {
            const tmpData = data.filter((item: any) => item !== newVal);
            selectedNames = controlDomain.filter((domain: any) => tmpData.includes(domain.code)).map((domain: any) => domain.value);
        }

        if (action === 'REFRESH') {
            selectedNames = controlDomain.filter((domain: any) => data.includes(domain.code)).map((domain: any) => domain.value);
        }

        if (selectedNames.length === 0) return 'Select';

        if (selectedNames.join(', ').length > 20) return `${selectedNames.length} selected`;

        return selectedNames.join(', ') ?? '';
    };

    const [selectedItemsLabel, setSelectedItemsLabel] = useState(getButtonText());

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        // setSelectedItemsLabel(getButtonText('', 'REFRESH'));
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (searchTerm) {
            const filtered = controlDomain.filter((domain: any) => domain.value.toLowerCase().includes(searchTerm.toLowerCase()));
            setFilteredDomain(filtered);
        } else {
            setFilteredDomain(controlDomain);
        }
    }, [searchTerm, controlDomain]);

    useEffect(() => {
        setSelectedItemsLabel(getButtonText('', 'REFRESH'));
    }, [data, controlDomain]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const errorMessages = validateFormField(control, event.target.value, state, control.props.label, dataKey);
        const eventType = event.target.checked ? 'CHECKBOX_ADD_NEW_RECORD_IN_ARRAY' : 'DELETE_RECORD_IN_SIMPLE_ARRAY';
        dispatch({ type: eventType, payload: { dataKey, name: control.id, value: event.target.value, errorMessages } });
        setSelectedItemsLabel(getButtonText(event.target.value, event.target.checked ? 'ADD' : 'REMOVE'));
    };

    const handleClearAll = () => {
        setSelectedItemsLabel('');
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey, value: [] } });
    };

    useEffect(() => {
        if (!hasParent) return;

        if (!data?.length) return;

        // valid child codes for current parent
        const validCodes = controlDomain.map(d => String(d.code));

        // remove invalid selections
        const cleanedData = data.filter((item: any) =>
            validCodes.includes(String(item))
        );

        if (cleanedData.length !== data.length) {
            dispatch({
                type: 'CONTROL_VALUE_CHANGE',
                payload: {
                    dataKey,
                    value: cleanedData
                }
            });
        }
    }, [parentId, controlDomain]);


    return (
        <div className="dropdown" ref={dropdownRef}>
            {/* Displaying undefined when label is not passed in grid */}
            {control.props.label && (
                <label htmlFor={control.id} className="form-label w-100">
                    {`${control.props.label} `}
                </label>
            )}
            
            <button className="form-select" type="button" onClick={() => setIsOpen(!isOpen)} style={{ textAlign: 'left' }}>
                {selectedItemsLabel}
            </button>

            {isOpen && (
                <div className="dropdown-menu w-100" style={{ display: 'block', background: 'white' }}>
                    <div className="dropdown-wrap">
                        <div className="p-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder={`Search ${control.props.label}`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <a role="button" className="dropdown-item" onClick={handleClearAll}>
                            Clear All
                        </a>
                        {filteredDomain?.length > 0 ? (
                            filteredDomain.map((domain: any) => (
                                <div key={domain.code} className="dropdown-item">
                                    <label>
                                        <input
                                            id={domain.code.toString()}
                                            type="checkbox"
                                            name={dataKey}
                                            className="form-check-input"
                                            value={domain.code}
                                            checked={data?.some((item: any) => item == domain.code)}
                                            onChange={(event) => handleChange(event)}
                                            ref={formControlRef}
                                        />
                                        {domain.value}
                                    </label>
                                </div>
                            ))
                        ) : (
                            <div className="dropdown-item text-muted">No results</div>
                        )}
                        <ErrorControl errorMessages={state?.formValidationErrors[dataKey]} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelectCustomControl2;
